import { dueMarkup } from '../../ui/routines.js';
/**
 * Dashboard View Module
 * Pure view: receives state, emits events via callbacks
 * Shows project overview, stats, deadlines, and progress
 *
 * Layout matches original:
 * Row 1: Upcoming Deadlines | Tasks In Progress | Calendar
 * Row 2: Overview (pie + stats + completion bar) | Progress by Section
 */

import { safeColor, escapeAttr } from "../../../../engine/core/validation.js";
import { isoWeekOf } from "../../../../engine/sync/execution-stats.js";
import { calendarDay, dateStringAtDay } from "../../../../engine/core/calendar-day.js";
import { t } from "../../../../shared/i18n/index.js";
import { viewLabel } from "../../../../engine/core/view-names.js";

// Module state
let container = null;
let callbacks = null;
let currentState = null;

// Batched rendering
let renderScheduled = false;

/**
 * Render the Dashboard view
 * @param {HTMLElement} containerEl - Container element
 * @param {Object} state - { data, focusId, selectedId, hideCompleted }
 * @param {Object} cbs - Callbacks for actions
 */
export function render(containerEl, state, cbs) {
  container = containerEl;
  callbacks = cbs;
  currentState = state;

  const escapeHtml = cbs.escapeHtml || ((s) => s);

  // Get all leaf tasks
  const allTasks = getAllLeaves(state.data);
  const stats = calculateStats(allTasks);
  const upcoming = getUpcomingDeadlines(allTasks);
  const inProgressTasks = getInProgressTasks(allTasks);
  const sections = getSectionProgress(state.data);

  container.innerHTML = `
    <div class="dashboard-container">
      <div class="dash-routines">${dueMarkup()}</div>
      <div class="dashboard-grid">
        <!-- Row 1: Upcoming Deadlines | Tasks In Progress | Calendar -->
        <div class="dash-card dash-upcoming">
          <div class="dash-card-title">${t("views.dash.upcoming")}</div>
          <div class="dash-upcoming-list" id="dash-upcoming">
            ${renderUpcoming(upcoming, escapeHtml)}
          </div>
        </div>

        <div class="dash-card dash-inprogress">
          <div class="dash-card-title">${t("views.dash.inProgressTitle")}</div>
          <div class="dash-inprogress-list" id="dash-inprogress">
            ${renderInProgress(inProgressTasks, escapeHtml)}
          </div>
        </div>

        <div class="dash-card dash-calendar-card">
          <div class="dash-calendar-header">
            <div class="dash-card-title">${t("views.dash.calendar")}</div>
            <button class="dash-calendar-config-btn" id="nc-config-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"></path>
              </svg>
              ${t("views.dash.configure")}
            </button>
          </div>
          <div id="nc-calendar-content">
            <div class="dash-calendar-setup">
              <div class="dash-calendar-setup-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </div>
              <p>${t("views.dash.calendarSetup")}</p>
              <button class="dash-calendar-setup-btn" id="nc-setup-btn">${t("views.dash.addCalendars")}</button>
            </div>
          </div>
        </div>

        <!-- Row 2: Overview | Progress by Section -->
        <div class="dash-card dash-overview-combined">
          <div class="dash-card-title">${t("views.dash.overview")}</div>
          <div class="dash-overview-content">
            <div class="dash-chart-section" id="dash-chart">
              ${renderPieChart(stats)}
            </div>
            <div class="dash-stats-section">
              <div class="dash-stat-row">
                <span class="dash-stat-label">${t("views.dash.total")}</span>
                <span class="dash-stat-value" id="dash-total">${stats.total}</span>
              </div>
              <div class="dash-stat-row done">
                <span class="dash-stat-label">${t("views.dash.completed")}</span>
                <span class="dash-stat-value" id="dash-done">${stats.done}</span>
              </div>
              <div class="dash-stat-row in-progress">
                <span class="dash-stat-label">${t("views.dash.inProgress")}</span>
                <span class="dash-stat-value" id="dash-progress">${stats.inProgress}</span>
              </div>
              <div class="dash-stat-row todo">
                <span class="dash-stat-label">${t("views.dash.todo")}</span>
                <span class="dash-stat-value" id="dash-todo">${stats.todo}</span>
              </div>
              <div class="dash-completion">
                <div class="dash-completion-bar">
                  <div class="dash-completion-fill" id="dash-completion-fill" style="width: ${stats.percent}%"></div>
                </div>
                <div class="dash-completion-text" id="dash-completion-text">${t("views.dash.percentComplete", { percent: stats.percent })}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="dash-card dash-wp-progress">
          <div class="dash-card-title">${t("views.dash.bySection")}</div>
          <div class="dash-wp-list" id="dash-wp-list">
            ${renderSectionProgress(sections, escapeHtml)}
          </div>
        </div>

        <!-- Row 3: Execution (derived from Today lists) -->
        ${renderExecutionCard(escapeHtml)}
      </div>
    </div>
  `;

  attachEventHandlers();
}

/**
 * Execution card — everything derived from the Today lists (execution-stats):
 * this period's load, allocation by top-level branch, worked days per week,
 * starved pinned items, closed-without-work count. Empty until the document
 * has execution data.
 */
function renderExecutionCard(escapeHtml) {
  const stats = callbacks?.getExecutionStats?.();
  if (!stats || !stats.periods.length) return "";
  const find = (id) => callbacks?.find?.(id) || null;
  const nameOf = (id) => { const n = find(id); return n ? (n.name || n.text || t("common.untitled")) : t("views.dash.missing"); };
  const cur = stats.currentPeriod;

  // Load line
  const load = cur?.load;
  const loadHtml = load && load.itemsTotal
    ? `<div class="dash-exec-load">
         <span class="dash-exec-load-label">${t("views.dash.currentListItems", { count: load.itemsTotal })}</span>
         <span class="dash-exec-load-value" title="${t("views.dash.estimateTitle")}">${t("views.dash.estimateLeft", { count: Math.round(load.remainingDays) })}</span>
       </div>`
    : `<div class="dash-exec-load"><span class="dash-exec-load-label">${t("views.dash.currentList")}</span><span class="dash-exec-load-value">${t("views.dash.nothingListed")}</span></div>`;

  // Allocation over the last 4 weeks by branch (ticks), as horizontal bars
  const weekMap = new Map(stats.weeks.map((w) => [w.week, w]));
  const today = calendarDay(new Date());
  const weeks = Array.from({ length: 8 }, (_, i) => {
    const week = isoWeekOf(dateStringAtDay(today - (7 - i) * 7));
    return weekMap.get(week) || { week, ticks: 0, done: 0, ticksByBranch: {} };
  });
  const byBranch = new Map();
  for (const w of weeks.slice(-4)) for (const [b, n] of Object.entries(w.ticksByBranch)) byBranch.set(b, (byBranch.get(b) || 0) + n);
  const totalTicks = [...byBranch.values()].reduce((a, b) => a + b, 0);
  const allocRows = [...byBranch.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([b, n]) => {
    const node = b === "(none)" ? null : find(b);
    const label = node ? (node.name || t("common.untitled")) : t("views.dash.noBranch");
    const color = node ? safeColor(node.color) : "#9ca3af";
    const pct = totalTicks ? Math.round((n / totalTicks) * 100) : 0;
    return `<div class="dash-exec-alloc-row" title="${t("views.dash.workedDaysRecent", { count: n })}">
      <span class="dash-exec-alloc-name">${escapeHtml(label)}</span>
      <span class="dash-exec-alloc-bar"><span style="width:${pct}%;background:${color}"></span></span>
      <span class="dash-exec-alloc-pct">${pct}%</span>
    </div>`;
  }).join("");
  const allocHtml = totalTicks
    ? `<div class="dash-exec-section-title">${t("views.dash.lastWeeks")}</div>${allocRows}`
    : `<div class="dash-exec-section-title">${t("views.dash.weeksGo")}</div><div class="dash-exec-empty">${t("views.dash.weeksEmpty")}</div>`;

  // Worked days per week (last 8 weeks) as small columns
  const maxTicks = Math.max(1, ...weeks.map((w) => w.ticks));
  const weeksHtml = weeks.length
    ? `<div class="dash-exec-section-title">${t("views.dash.workedPerWeek")}</div>
       <div class="dash-exec-weeks">${weeks.map((w) => `
         <div class="dash-exec-week" title="${t("views.dash.weekTitle", { week: w.week, count: w.ticks, done: w.done })}">
           <div class="dash-exec-week-bar"><div style="height:${Math.round((w.ticks / maxTicks) * 100)}%"></div></div>
           <div class="dash-exec-week-label">${w.week.slice(-3)}</div>
         </div>`).join("")}</div>`
    : "";

  // Starvation + closed without work
  const starved = stats.starvation.map((s) => `<li>${escapeHtml(nameOf(s.nodeId))} <span class="dash-exec-muted">${t("views.dash.starved", { count: s.periodsWithoutWork })}</span></li>`).join("");
  const closedNoWork = Object.values(stats.nodes).filter((n) => n.closedWithoutWork).length;
  const signalsHtml = (starved || closedNoWork)
    ? `<div class="dash-exec-section-title">${t("views.dash.signals")}</div>
       <ul class="dash-exec-signals">${starved}${closedNoWork ? `<li><span class="dash-exec-muted">${t("views.dash.closedNoWork", { count: closedNoWork })}</span></li>` : ""}</ul>`
    : "";

  return `<div class="dash-card dash-execution">
    <div class="dash-card-title">${t("views.dash.execution")}</div>
    ${loadHtml}
    <div class="dash-exec-columns">
      <div>${allocHtml}</div>
      <div>${weeksHtml}</div>
    </div>
    ${signalsHtml}
  </div>`;
}

/**
 * Get all leaf nodes (tasks without children)
 */
function getAllLeaves(rootNode) {
  const leaves = [];
  // rootId is dynamic (not always "root"), e.g., shared docs
  const rootId = rootNode?.id;

  function traverse(node) {
    if (!node) return;
    if (node.id === rootId) {
      (node.children || []).forEach(traverse);
      return;
    }
    if (!node.children || node.children.length === 0) {
      leaves.push(node);
    } else {
      node.children.forEach(traverse);
    }
  }

  traverse(rootNode);
  return leaves;
}

/**
 * Calculate task statistics
 */
function calculateStats(tasks) {
  const total = tasks.length;
  const done = tasks.filter((t) => callbacks?.nodeIsDone?.(t) || t.status === "done").length;
  const inProgress = tasks.filter((t) => callbacks?.nodeIsInProgress?.(t) || t.status === "in-progress").length;
  const todo = total - done - inProgress;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  return { total, done, inProgress, todo, percent };
}

/**
 * Get upcoming deadlines (next 14 days)
 */
function getUpcomingDeadlines(tasks) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const twoWeeks = new Date(today);
  twoWeeks.setDate(twoWeeks.getDate() + 14);

  return tasks
    .filter((t) => t.endDate && !(callbacks?.nodeIsDone?.(t) || t.status === "done"))
    .map((t) => ({ ...t, endDateObj: new Date(t.endDate) }))
    .filter((t) => t.endDateObj <= twoWeeks)
    .sort((a, b) => a.endDateObj - b.endDateObj)
    .slice(0, 8);
}

/**
 * Get in-progress tasks sorted by deadline
 */
function getInProgressTasks(tasks) {
  return tasks
    .filter((t) => callbacks?.nodeIsInProgress?.(t) || t.status === "in-progress")
    .sort((a, b) => {
      if (a.endDate && b.endDate) {
        return new Date(a.endDate) - new Date(b.endDate);
      }
      if (a.endDate) return -1;
      if (b.endDate) return 1;
      return (a.name || a.text || "").localeCompare(b.name || b.text || "");
    });
}

/**
 * Get progress by section (top-level children)
 */
function getSectionProgress(rootNode) {
  const sections = rootNode.children || [];
  return sections.map((section) => {
    const leaves = getLeaves(section);
    const total = leaves.length;
    const done = leaves.filter((t) => callbacks?.nodeIsDone?.(t) || t.status === "done").length;
    const percent = total > 0 ? Math.round((done / total) * 100) : 0;

    return {
      id: section.id,
      name: section.name || section.text || t("common.untitled"),
      color: section.color || "#6b7280",
      total,
      done,
      percent,
    };
  });
}

/**
 * Get all leaf descendants of a node
 */
function getLeaves(node) {
  if (!node.children || node.children.length === 0) return [node];
  return node.children.flatMap(getLeaves);
}

/**
 * Render the pie chart
 */
function renderPieChart(stats) {
  if (stats.total === 0) {
    return '<div class="dash-pie dash-pie-empty"><div class="dash-pie-center"></div></div>';
  }

  const donePercent = (stats.done / stats.total) * 100;
  const progressPercent = (stats.inProgress / stats.total) * 100;

  const gradient = `conic-gradient(
    #22c55e 0% ${donePercent}%,
    #3b82f6 ${donePercent}% ${donePercent + progressPercent}%,
    #94a3b8 ${donePercent + progressPercent}% 100%
  )`;

  return `
    <div class="dash-pie" style="background: ${gradient}">
      <div class="dash-pie-center"></div>
    </div>
  `;
}

/**
 * Render section progress list
 */
function renderSectionProgress(sections, escapeHtml) {
  if (sections.length === 0) {
    return `<div class="dash-empty">${t("views.dash.noSections")}</div>`;
  }

  return sections
    .map(
      (section) => `
    <div class="dash-wp-item">
      <div class="dash-wp-header">
        <div class="dash-wp-name">
          <span class="dash-wp-dot" style="background: ${safeColor(section.color)}"></span>
          ${escapeHtml(section.name)}
        </div>
        <div class="dash-wp-percent">${section.done}/${section.total} (${section.percent}%)</div>
      </div>
      <div class="dash-wp-bar">
        <div class="dash-wp-fill" style="width: ${section.percent}%; background: ${safeColor(section.color)}"></div>
      </div>
    </div>
  `,
    )
    .join("");
}

/**
 * Render upcoming deadlines
 */
function renderUpcoming(upcoming, escapeHtml) {
  if (upcoming.length === 0) {
    return `<div class="dash-empty">${t("views.dash.noUpcoming")}</div>`;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return upcoming
    .map((task) => {
      const endDate = new Date(task.endDate);
      endDate.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));

      let dateClass = "";
      let dateText = "";
      if (diffDays < 0) {
        dateClass = "overdue";
        dateText = t("views.dash.overdue", { days: Math.abs(diffDays) });
      } else if (diffDays === 0) {
        dateClass = "today";
        dateText = t("common.today");
      } else if (diffDays === 1) {
        dateText = t("common.tomorrow");
      } else {
        dateText = t("views.dash.inDays", { count: diffDays });
      }

      const isIP = callbacks?.nodeIsInProgress?.(task) || task.status === "in-progress";
      const statusClass = isIP ? "in-progress" : "";
      const statusText = isIP ? t("views.dash.inProgress") : t("views.dash.todo");
      const taskName = task.name || task.text || t("common.untitled");

      return `
      <div class="dash-upcoming-item" data-id="${escapeAttr(task.id)}" style="border-left-color: ${safeColor(task.color, "#6b7280")}">
        <div class="dash-upcoming-date ${dateClass}">${dateText}</div>
        <div class="dash-upcoming-name">${escapeHtml(taskName)}</div>
        <div class="dash-upcoming-status ${statusClass}">${statusText}</div>
      </div>
    `;
    })
    .join("");
}

/**
 * Render in-progress tasks
 */
function renderInProgress(tasks, escapeHtml) {
  if (tasks.length === 0) {
    return `<div class="dash-empty">${t("views.dash.noInProgress")}</div>`;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return tasks
    .map((task) => {
      let dateText = "";
      if (task.endDate) {
        const endDate = new Date(task.endDate);
        endDate.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) {
          dateText = t("views.dash.overdue", { days: Math.abs(diffDays) });
        } else if (diffDays === 0) {
          dateText = t("views.dash.dueToday");
        } else if (diffDays === 1) {
          dateText = t("views.dash.dueTomorrow");
        } else {
          dateText = t("views.dash.daysLeft", { days: diffDays });
        }
      }

      const taskName = task.name || task.text || t("common.untitled");

      return `
      <div class="dash-inprogress-item" data-id="${escapeAttr(task.id)}" style="border-left-color: ${safeColor(task.color, "#6b7280")}">
        <div class="dash-inprogress-name">${escapeHtml(taskName)}</div>
        ${dateText ? `<div class="dash-inprogress-date">${dateText}</div>` : ""}
      </div>
    `;
    })
    .join("");
}

/**
 * Attach event handlers
 */
function attachEventHandlers() {
  if (!container) return;

  // Click on upcoming/in-progress item - open edit modal
  container.addEventListener("click", (e) => {
    const upcomingItem = e.target.closest(".dash-upcoming-item");
    const inProgressItem = e.target.closest(".dash-inprogress-item");

    const item = upcomingItem || inProgressItem;
    if (item) {
      const id = item.dataset.id;
      if (callbacks.onNodeEdit) {
        callbacks.onNodeEdit(id);
      }
    }
  });

  // Calendar configuration buttons (placeholders for now)
  const configBtn = container.querySelector("#nc-config-btn");
  const setupBtn = container.querySelector("#nc-setup-btn");

  if (configBtn) {
    configBtn.addEventListener("click", () => {
      console.log("Calendar configuration - not yet implemented");
    });
  }

  if (setupBtn) {
    setupBtn.addEventListener("click", () => {
      console.log("Calendar setup - not yet implemented");
    });
  }
}

/**
 * Schedule a render for the next animation frame (batches multiple requests)
 */
function scheduleRender() {
  if (renderScheduled) return;
  renderScheduled = true;

  requestAnimationFrame(() => {
    renderScheduled = false;
    if (container && callbacks && currentState) {
      render(container, currentState, callbacks);
    }
  });
}

/**
 * Update the view with new state
 */
export function update(prevState, nextState) {
  currentState = nextState;
  if (container && callbacks) {
    scheduleRender();
  }
}

/**
 * Clean up the view
 */
export function destroy() {
  if (container) {
    container.innerHTML = "";
  }
  container = null;
  callbacks = null;
  currentState = null;
  renderScheduled = false;
}

// Export view info
export const viewInfo = {
  name: "dashboard",
  get label() { return viewLabel("dashboard"); },
  icon: "chart-pie",
};
